import React, { useState } from "react";
import styles from "./EnterRunOffWinnerModal.module.scss";
import Button from "../Button/Button";
import Dropdown, { IOption } from "../Dropdown/Dropdown";

interface ITiedScore {
  arm_number: string;
  dog_name: string;
  score_id: string;
  final_score?: number; // The score they tied with
}

interface IProps {
  runOffIndex: number;
  showElement: string;
  level: string;
  placement: string;
  tiedScores: ITiedScore[];
  tiedScore?: number; // The score they're tied with (200, 195, etc)
  onSubmit: (orderedPlacements: Array<{ arm_number: string; dog_name: string; score_id: string; placement: number }>) => Promise<void>;
  onCancel: () => void;
}

const rowKey = (score: { score_id?: string; arm_number?: string }, index: number): string =>
  score.score_id || `${score.arm_number || "no-arm"}-${index}`;

const EnterRunOffWinnerModal: React.FC<IProps> = ({
  runOffIndex,
  showElement,
  level,
  placement,
  tiedScores,
  tiedScore,
  onSubmit,
  onCancel,
}) => {
  const [orderedPlacements, setOrderedPlacements] = useState<Array<{ key: string; arm_number: string; score_id: string; placement: number }>>(
    tiedScores.map((score, index) => ({
      key: rowKey(score, index),
      arm_number: score.arm_number,
      score_id: score.score_id,
      placement: 0,
    }))
  );
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handlePlacementChange = (key: string, placementValue: number) => {
    setOrderedPlacements(prev =>
      prev.map(p => p.key === key ? { ...p, placement: placementValue } : p)
    );
    setError(""); // Clear error when user makes changes
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate that all placements are assigned
    const hasUnassigned = orderedPlacements.some(p => p.placement === 0);
    if (hasUnassigned) {
      setError("Please assign a placement to all dogs");
      return;
    }

    // Validate that placements are unique and consecutive starting from 1
    const placements = orderedPlacements.map(p => p.placement).sort((a, b) => a - b);
    const expectedPlacements = Array.from({ length: placements.length }, (_, i) => i + 1);
    const isValid = placements.every((p, i) => p === expectedPlacements[i]);

    if (!isValid) {
      setError("Placements must be consecutive starting from 1 (e.g., 1st, 2nd, 3rd)");
      return;
    }

    setIsSubmitting(true);
    try {
      // Build ordered_placements array with full dog info
      const orderedPlacementsWithInfo = orderedPlacements.map(op => {
        const tiedScore = tiedScores.find((ts, index) => rowKey(ts, index) === op.key);
        return {
          arm_number: op.arm_number,
          dog_name: tiedScore?.dog_name || "",
          score_id: op.score_id || tiedScore?.score_id || "",
          placement: op.placement
        };
      });

      await onSubmit(orderedPlacementsWithInfo);
    } catch (err: any) {
      setError(err.message || "Failed to submit placements");
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.EnterRunOffWinnerModal}>
      <div className={styles.header}>
        <h2>Enter Run-Off Winner</h2>
        <p className={styles.subtitle}>
          {level} - {placement} Place
        </p>
      </div>

      <div className={styles.content}>
        <div className={styles.tiedDogs}>
          <h3>
            The following dogs are tied{tiedScore ? ` with a score of ${tiedScore}` : ''}:
          </h3>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Assign Placements:</label>
            <div className={styles.placementsList}>
              {tiedScores.map((score, index) => {
                const key = rowKey(score, index);
                const currentPlacement = orderedPlacements.find(
                  p => p.key === key
                )?.placement || 0;

                const placementOptions: IOption[] = [
                  { id: "0", value: "Select placement..." },
                  ...tiedScores.map((_, idx) => ({
                    id: String(idx + 1),
                    value: `${idx + 1}${idx === 0 ? 'st' : idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'} Place`
                  }))
                ];

                const selectedOption = placementOptions.find(opt => opt.id === String(currentPlacement)) || placementOptions[0];

                return (
                  <div key={key} className={styles.placementRow}>
                    <span className={styles.dogInfo}>
                      <span className={styles.armNumber}>{score.arm_number ? `#${score.arm_number}` : ""}</span>
                      <span className={styles.dogName}>{score.dog_name}</span>
                    </span>
                    <Dropdown
                      id={`runoff-placement-${key}`}
                      value={selectedOption}
                      onChange={(e: any, value: IOption) => handlePlacementChange(key, parseInt(value.id))}
                      options={placementOptions}
                      placeholder="Select placement..."
                      label="Placement"
                      className={styles.placementSelect}
                      disabled={isSubmitting}
                    />
                  </div>
                );
              })}
            </div>
            {error && <div className={styles.error}>{error}</div>}
          </div>

          <div className={styles.actions}>
            <Button
              type="button"
              onClick={onCancel}
              className={styles.cancelButton}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className={styles.submitButton}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              Save Placements
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnterRunOffWinnerModal;

